import { Dialog, DialogContent, DialogProps, DialogTitle } from '@material-ui/core';
import { Form, Formik, FormikHelpers } from 'formik';
import { CancelSubmitFormDialogActions } from 'framework/forms/CancelSubmitFormDialogActions';
import { FormSelectBooleanField } from 'framework/forms/FormSelectBooleanField';
import { handleFormResponse } from 'framework/forms/utils/handleFormResponse';
import { useFormSubmit } from 'framework/hooks/useFormSubmit';
import { IPatchPeppolSettingsRequest, IPeppolSettings, patchPeppolSettings_handle, PeppolRecordStatus } from 'gen/ApiClient';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import React, { useContext, useMemo } from 'react';
import * as yup from 'yup';
import { ChipX, ChipXProps } from 'framework/components/chips/ChipX';
import { RecordContext } from 'shared/records/RecordContext';
import { ColorX } from 'framework/components/color/ColorX';
import { PeppolRecordsQueryProvider } from './peppol-records.tab';

const createSchema = (_: IStrings) => {
	return yup.object<IPatchPeppolSettingsRequest>({
		autoSend: yup.boolean().required(),
		waitForDocument: yup.boolean().required(),
	});
};

type PatchPeppolSettingsDialogProps = DialogProps & {
	confirm: () => void;
	cancel: VoidFunction;
	settings: IPeppolSettings;
};

export const PatchPeppolSettingsDialog = ({ confirm, cancel, settings, ...rest }: PatchPeppolSettingsDialogProps) => {
	const strings = useLocalization();
	const [submit, isSubmitting] = useFormSubmit(patchPeppolSettings_handle);
	const schema = useMemo(() => createSchema(strings), [strings]);

	const handleSubmit = async (values: IPatchPeppolSettingsRequest, helpers: FormikHelpers<IPatchPeppolSettingsRequest>) => {
		const r = await submit(values);
		if (handleFormResponse(r, helpers)) {
			confirm();
		}
	};

	return (
		<Formik<IPatchPeppolSettingsRequest>
			validateOnMount
			initialValues={{
				autoSend: settings.autoSend,
				waitForDocument: settings.waitForDocument,
			}}
			validationSchema={schema}
			onSubmit={handleSubmit}>
			<Form>
				<Dialog {...rest}>
					<DialogTitle>{`${strings.settings} PEPPOL`}</DialogTitle>
					<DialogContent
						dividers
						className='df-col'
						style={{ minWidth: 400 }}>
						<FormSelectBooleanField<IPatchPeppolSettingsRequest>
							label={`${strings.autoSend}?`}
							name='autoSend'
							trueText={strings.invoicesWillBeAutomaticallyForwardedToPeppol}
							falseText={strings.invoicesWillNotBeAutomaticallyForwardedToPeppol}
						/>
						<FormSelectBooleanField<IPatchPeppolSettingsRequest>
							label={`${strings.withOrWithoutDocumentAutoForward}?`}
							name='waitForDocument'
							trueText={strings.createDocumentFirstThanForward}
							falseText={strings.forwardWithoutDocument}
						/>
					</DialogContent>
					<CancelSubmitFormDialogActions
						cancel={cancel}
						submitText={strings.update}
						isSubmitting={isSubmitting}
					/>
				</Dialog>
			</Form>
		</Formik>
	);
};

export const PeppolProviders = ({ children }: any) => {
	return <PeppolRecordsQueryProvider>{children}</PeppolRecordsQueryProvider>;
};

export const PeppolRecordStatusColorRecord: Record<PeppolRecordStatus, ColorX> = {
	Failed: 'error',
	Invalid: 'warning',
	New: 'info',
	Sending: 'info',
	Sent: 'success',
	Ignored: 'info',
};

type PeppolRecordStatusChipProps = Omit<ChipXProps, 'label'> & {
	status: PeppolRecordStatus;
};

export const PeppolRecordStatusChip = ({ status, ...rest }: PeppolRecordStatusChipProps) => {
	const { peppolRecordStatusRecord } = useContext(RecordContext);

	return (
		<ChipX
			label={peppolRecordStatusRecord[status]}
			color={PeppolRecordStatusColorRecord[status]}
			{...rest}
		/>
	);
};

export const createPeppolRecordStatusRecord = (strings: IStrings): Record<PeppolRecordStatus, string> => {
	return {
		Failed: strings.failed,
		Invalid: strings.invalid,
		New: strings.new,
		Sending: strings.sending,
		Sent: strings.sent,
		Ignored: strings.ignored,
	};
};
